import _ from 'lodash';
import numeral from 'numeral';
import tinycolor from 'tinycolor2';
import { getShapeCentroid, getLargestPart, getLargestPartLabelPoint, getVisibleCentroid, boundsToTurfPolygon } from './VectorLayerUtils';
import { applyFeatureTagStyle, getSavedTagStyles } from './TagStyleStorage';

// import 'mapbox.js';

export class TagsLayer {
    constructor({ features, getStyle, layer, options, map }) {
        this.featureGroup = L.featureGroup();
        this.features = features;
        this.getStyle = getStyle;
        this.layer = layer;
        this.map = map;
        this.options = options;
        this._clampIndex = [];
        this._clampPadding = 0.85;

        this.add();

        if (this.options.tagViewportClamp && this.map) {
            this._onMoveEnd = _.debounce(() => this._repositionForViewport(), 150);
            this.map.on('moveend', this._onMoveEnd);
            this._repositionForViewport();
        }
    }

    add() {
        // Per-feature tag style overrides are read once per render; the click
        // handler below lets the user edit the name tag of a single feature.
        this._tagStyles = this.options.tagStyleScope ? getSavedTagStyles(this.options.tagStyleScope) : {};

        /**
         * Draws a rounded rectangle using the current state of the canvas.
         * If you omit the last three params, it will draw a rectangle
         * outline with a 5 pixel border radius
         * @param {CanvasRenderingContext2D} ctx
         * @param {Number} x The top left x coordinate
         * @param {Number} y The top left y coordinate
         * @param {Number} width The width of the rectangle
         * @param {Number} height The height of the rectangle
         * @param {Number} [radius = 5] The corner radius; It can also be an object
         *                 to specify different radii for corners
         * @param {Number} [radius.tl = 0] Top left
         * @param {Number} [radius.tr = 0] Top right
         * @param {Number} [radius.br = 0] Bottom right
         * @param {Number} [radius.bl = 0] Bottom left
         */

        const addRectangleToCanvas = (ctx, x, y, width, height, radius = 5) => {
            if (typeof radius === 'number') {
                radius = { tl: radius, tr: radius, br: radius, bl: radius };
            } else {
                const defaultRadius = { tl: 0, tr: 0, br: 0, bl: 0 };
                for (let side in defaultRadius) {
                    radius[side] = radius[side] || defaultRadius[side];
                }
            }
            ctx.beginPath();
            ctx.moveTo(x + radius.tl, y);
            ctx.lineTo(x + width - radius.tr, y);
            ctx.quadraticCurveTo(x + width, y, x + width, y + radius.tr);
            ctx.lineTo(x + width, y + height - radius.br);
            ctx.quadraticCurveTo(x + width, y + height, x + width - radius.br, y + height);
            ctx.lineTo(x + radius.bl, y + height);
            ctx.quadraticCurveTo(x, y + height, x, y + height - radius.bl);
            ctx.lineTo(x, y + radius.tl);
            ctx.quadraticCurveTo(x, y, x + radius.tl, y);
            ctx.closePath();
        };

        const _getTextStyle = ({ size = 9, bold, italic }) => {
            const hasBold = bold ? 'bold ' : '';
            const hasItalic = italic ? 'italic ' : '';
            return {
                font: `${hasItalic}${hasBold}${size}px 'Roboto Condensed', Arial, Sans-serif`,
                fontSize: size,
                strokeWeight: Math.floor(size * 0.2)
            };
        };

        const _getLabelPosition = (layer, options) => {
            options.align = options.align || 'center';
            const feature = _getFeature(layer, options);

            if (feature == null)
                return;

            if (layer.getBounds) {
                const positions = {
                    centroid(a, b, c) {
                        const cx = (a.lng + b.lng + c.lng) / 3;
                        const cy = (a.lat + b.lat + c.lat) / 3;
                        return L.latLng([cy, cx]);
                    },
                    center: () => this._getNaturalLatLng(feature),
                    east() {
                        return L.latLng(this.center().lat, layer.getBounds().getEast());
                    },
                    west() {
                        return L.latLng(this.center().lat, layer.getBounds().getWest());
                    },
                    north() {
                        return L.latLng(layer.getBounds().getNorth(), this.center().lng);
                    },
                    south() {
                        return L.latLng(layer.getBounds().getSouth(), this.center().lng);
                    },
                    northEast() {
                        return this.centroid(this.north(), this.east(), layer.getBounds().getNorthEast());
                    },
                    northWest() {
                        return this.centroid(this.north(), this.west(), layer.getBounds().getNorthWest());
                    },
                    southEast() {
                        return this.centroid(this.south(), this.east(), layer.getBounds().getSouthEast());
                    },
                    southWest() {
                        return this.centroid(this.south(), this.west(), layer.getBounds().getSouthWest());
                    }
                };
                try {
                    return positions[options.align]();
                } catch (err) {
                    console.log(err);
                    return positions.center();
                }
            } else if (feature) {
                return getShapeCentroid(feature);
            } else {
                return layer.getLatLng();
            }
        };

        const _getFeature = (layer, options) => {
            if (options && layer._childCount) {
                const markers = !layer._markers.length ? layer._childClusters[0]._markers : layer._markers;

                if (markers && markers.length > 1
                    && markers.every((m, _, arr) => m.feature.properties[options.property] === arr[0].feature.properties[options.property])) {
                    return markers[0].feature;
                }

                return null;
            } else {
                return layer.feature;
            }
        };

        const _drawLabel = (layer, options) => {
            const feature = _getFeature(layer, options);

            if (feature == null)
                return;

            if (feature.properties.notPaint) {
                return;
            }

            const editableProperties = [].concat(this.options.tagStyleProperty || []);
            const canEditFeatureTag = Boolean(this.options.tagStyleScope)
                && _.isObject(options)
                && editableProperties.indexOf(options.property) !== -1;
            const labelOptions = canEditFeatureTag
                ? applyFeatureTagStyle(options, feature, this._tagStyles, this.options.uniqueField)
                : options;

            const canvas = L.DomUtil.create('canvas', 'leaflet-label-overlay');
            const ctx = canvas.getContext('2d');

            let style;
            let text;
            let x;
            let y;
            let offsetY = 0;
            let offsetX = 0;

            if (_.isObject(labelOptions)) {
                // if (feature.properties[options.property] === 0) return;

                style = _getTextStyle(labelOptions);
                text = feature.properties[labelOptions.property];

                if (labelOptions.ignore && labelOptions.ignore(text)) {
                    return;
                }

                if (labelOptions.replace) {
                    const replaceText = _.isFunction(labelOptions.replace.value) ? labelOptions.replace.value() : labelOptions.replace.value;
                    const replaceRegex = _.isFunction(labelOptions.replace.regex) ? labelOptions.replace.regex() : labelOptions.replace.regex;
                    text = text.replace(replaceRegex, replaceText);
                }

                if (labelOptions.mask) {
                    if (_.isFunction(labelOptions.mask)) {
                        text = labelOptions.mask(text);
                    } else if (_.isNumber(text)) {
                        if (labelOptions.mask) {
                            text = numeral(text).format(labelOptions.mask);
                        } else {
                            text = numeral(text).format('0,0[.]00');
                        }
                    }
                }

                x = labelOptions.x;
                y = labelOptions.y;
                offsetY = (labelOptions.offsetY || 0);
                offsetX = (labelOptions.offsetX || 0);
            } else {
                style = _getTextStyle();
                text = feature.properties[labelOptions];
            }

            if (!text || _.some(['undefined', 'null'], v => v === String(text))) {
                return;
            }

            ctx.font = style.font;
            canvas.width = ctx.measureText(text).width + 20;
            canvas.height = style.fontSize * 2;

            const isIcon = layer instanceof L.Marker && (layer.options.icon instanceof L.Icon || layer.options.icon instanceof L.MarkerCluster);

            if (isIcon) {
                let { iconSize } = layer.options.icon.options;

                if (!iconSize) {
                    iconSize = [40 * layer._childCount, 5];
                }

                const iconAnchor = layer.options.icon.options.iconAnchor || [iconSize[0] / 2, iconSize[1] / 2];
                const isStacked = layer.options.icon.options.stacked;

                if (isStacked) {
                    offsetX = 0;
                    offsetY = 0;
                }

                if (isStacked && labelOptions.align !== 'south') {
                    x = (-iconSize[0] / 2) - 5;
                } else if (labelOptions.align === 'west') {
                    x = canvas.width + (iconSize[0] / 2) + 5;
                } else if (labelOptions.align === 'south') {
                    x = canvas.width / 2;
                } else {
                    x = (-iconSize[0] / 2) - 5;
                }

                if (isStacked && labelOptions.align !== 'south') {
                    y = (canvas.height / 2) + iconAnchor[1] - (iconSize[1] / 2) - ((labelOptions.stackedRow || 0) * 22);
                } else if (labelOptions.align === 'south') {
                    y = -((iconSize[1] - iconAnchor[1]) + 2);
                } else if (iconSize[0] === iconSize[1]) {
                    y = (canvas.height / 2);
                } else {
                    y = ((canvas.height / 2) + (iconSize[1] / 2));
                }
            } else {
                if (!x) {
                    x = canvas.width / 2;
                }

                if (!y) {
                    y = canvas.height / 2;
                }
            }

            // OFFSET
            x = Math.floor((x || 0) + offsetX);
            y = Math.floor((y || 0) + offsetY);

            // BACKGROUND
            if (labelOptions && labelOptions.box) {
                addRectangleToCanvas(ctx, 0, 0, canvas.width, canvas.height, 3);
                if (labelOptions.colorize) {
                    if (labelOptions.color && labelOptions.type === 'property') {
                        const c = feature.properties[labelOptions.color];
                        ctx.fillStyle = c;
                    } else if (labelOptions.color && labelOptions.color !== 'auto') {
                        ctx.fillStyle = tinycolor(labelOptions.color);
                    } else {
                        const featureStyle = this.getStyle(feature);
                        const color = tinycolor(featureStyle.fillColor || featureStyle.color || '#303030');
                        ctx.fillStyle = color.toRgbString();
                    }
                } else {
                    ctx.fillStyle = '#FFF';
                }
                ctx.fill();
                ctx.lineWidth = 1;
                ctx.strokeStyle = '#C2C2C2';
                ctx.stroke();
            } else {
                ctx.fillStyle = '#FFF';
                ctx.lineWidth = style.strokeWeight;
                ctx.lineCap = 'round';
                ctx.strokeStyle = 'rgba(250,250,250,.92)';
                ctx.textAlign = 'center';
                ctx.font = style.font;
                ctx.strokeText(text, canvas.width / 2, canvas.height / 1.5);
            }

            // FONT COLOR
            if (_.isObject(labelOptions)) {
                if ((labelOptions.color && labelOptions.color !== 'auto') && !labelOptions.colorize) {
                    ctx.fillStyle = labelOptions.color;
                } else {
                    ctx.fillStyle = tinycolor(ctx.fillStyle).isDark() ? '#FFF' : '#303030';
                }
            } else {
                ctx.fillStyle = '#303030';
            }

            // TEXT
            ctx.textAlign = 'center';
            ctx.font = style.font;
            ctx.fillText(text, canvas.width / 2, canvas.height / 1.5);

            const label = L.icon({
                iconUrl: canvas.toDataURL('image/png'),
                iconSize: [canvas.width, canvas.height],
                iconAnchor: [x, y]
            });

            const latlng = _getLabelPosition(layer, labelOptions);
            const marker = L.marker(latlng, { icon: label, riseOnHover: true });

            if (canEditFeatureTag && this.options.tagEditable && _.isFunction(this.options.onTagEdit)) {
                marker.feature = feature;
                marker.on('click', e => {
                    L.DomEvent.stopPropagation(e);
                    this.options.onTagEdit(feature, options);
                });
            }

            this.featureGroup.addLayer(marker);

            return marker;
        };

        const _processLabel = layer => {
            const markers = [];
            const collect = marker => {
                if (marker) {
                    markers.push(marker);
                }
            };

            if (_.isArray(this.options.tag)) {
                _.forEach(this.options.tag, value => collect(_drawLabel(layer, value)));
            } else {
                collect(_drawLabel(layer, this.options.tag));
            }

            if (this.options.tagViewportClamp && markers.length && layer.getBounds) {
                const tagOption = _.isArray(this.options.tag) ? this.options.tag[0] : this.options.tag;
                const feature = _getFeature(layer, tagOption);

                if (feature) {
                    this._clampIndex.push({
                        ...this._getClampTarget(feature, layer),
                        naturalLatLng: this._getNaturalLatLng(feature),
                        markers
                    });
                }
            }
        };

        if (this.layer instanceof L.MarkerClusterGroup) {
            _(this.layer._featureGroup._layers)
                .forEach(l => _processLabel(l));
        } else if (this.layer && _.isFunction(this.layer.getLayers)) {
            _.forEach((this.layer.getLayers() || []), l => _processLabel(l));
        }

        this.map.addLayer(this.featureGroup);
    }

    _getNaturalLatLng(feature) {
        return this.options.tagLargestPart ? getLargestPartLabelPoint(feature) : getShapeCentroid(feature);
    }

    _getClampTarget(feature, layer) {
        const target = { feature, featureBounds: layer.getBounds() };

        if (!this.options.tagLargestPart || _.get(feature, 'geometry.type') !== 'MultiPolygon') {
            return target;
        }

        try {
            const largest = getLargestPart(feature);
            return {
                feature: largest,
                featureBounds: L.geoJSON(largest).getBounds()
            };
        } catch (err) {
            console.log(err);
            return target;
        }
    }

    _repositionForViewport() {
        if (!this.map || !this._clampIndex || !this._clampIndex.length) {
            return;
        }

        const bounds = this.map.getBounds();
        const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()];
        let viewportPolygon;

        this._clampIndex.forEach(entry => {
            const { feature, naturalLatLng, featureBounds, markers } = entry;

            if (!naturalLatLng || !markers.length) {
                return;
            }

            // Whole area visible: keep the tag at its natural centroid.
            if (!featureBounds || bounds.contains(featureBounds)) {
                markers.forEach(marker => {
                    if (!marker.getLatLng().equals(naturalLatLng)) {
                        marker.setLatLng(naturalLatLng);
                    }
                });
                return;
            }

            // Area entirely off-screen: nothing to reposition.
            if (!bounds.intersects(featureBounds)) {
                return;
            }

            if (!viewportPolygon) {
                viewportPolygon = boundsToTurfPolygon(bbox, this._clampPadding);
            }

            const coords = getVisibleCentroid(feature, viewportPolygon);
            if (!coords) {
                return;
            }

            const latlng = L.latLng(coords[1], coords[0]);
            markers.forEach(marker => marker.setLatLng(latlng));
        });
    }

    remove() {
        if (this._onMoveEnd && this.map) {
            this.map.off('moveend', this._onMoveEnd);
            this._onMoveEnd.cancel();
            this._onMoveEnd = null;
        }

        if (this.featureGroup && this.map.hasLayer(this.featureGroup)) {
            this.map.removeLayer(this.featureGroup);
        }
    }
}



// WEBPACK FOOTER //
// ./src/scripts/layers/Leaflet/VectorLayer/TagLayer.js